// client/src/pages/sections/groups/SgReviewPanel.js
//
// The Signer Group Review step in the Client Groups panel. Lists
// every active SG the partner can see, sorted by anchor client code,
// with a search box and an expandable per-SG drilldown that mirrors
// the P1S1 issue-expansion (MemberCards with Change/Create New SG,
// Make SG Parent, Mark NLC). No required per-group actions — browse + edit,
// then the SG sign-off at the bottom gates the RG step.
//
// Sits between Phase 2 and Phase 3 in DataQualityPanel's rendering;
// is shown to the partner as "Step 3" with the existing RG and
// Standalone phases relabeled to Step 4 and Step 5.
//
// RG-modifying buttons are NOT shown here (allowRgActions=false)
// because RG editing is gated until Phase 3 unlocks — the same
// strict-phase model applied everywhere else in the panel.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MemberCard } from './DataQualityPanel';
import { useOptimistic } from './optimisticContext';
import PhaseSignoffBar from './PhaseSignoffBar';
import { emitToast, errorText } from './toast';
import ClientChip, { rememberClientNames } from './ClientChip';

export default function SgReviewPanel({
  scope, period, email, isAdmin, demo = false, testMode, testSessionId,
  // Step label / number for the header — passed in so DataQualityPanel
  // owns the numbering (renumbering elsewhere doesn't touch this file).
  stepNumber,
  // Non-null string when the step is gated (Phase 1 or 2 still has
  // open issues). The panel renders the locked-state chrome and
  // skips the data fetch so we don't load unused data behind the gate.
  gateReason,
  // SG phase signoff — rendered at the bottom of this step. Attesting
  // it unlocks the RG phase (Step 4). null until signed off.
  sgSignoff,
  // Called after the signoff toggles so the parent refetches dgGroupsDQ
  // and the gating chain (which reads signoffs.sg) updates.
  onSignoffChanged,
  // Called after a member action (split / Make SG Parent / Mark NLC) so the parent
  // refetches dgGroupsDQ too: the action can open a Step 1/2 issue or change the counts and
  // gating chain, none of which this panel's own dgSgReview list knows about. Silent.
  onDataChanged,
}) {
  const gated = Boolean(gateReason);
  const signedOff = Boolean(sgSignoff);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(!gated);
  const [err,     setErr]     = useState(null);
  const [search,  setSearch]  = useState('');

  // Once Step 3 is signed off, the long browse list is just noise — the
  // attestation is done. Collapse the
  // step to a compact confirmation strip, but keep it re-expandable (and
  // skip the SG-list fetch while collapsed). Initialize collapsed when we
  // mount already signed off; flip on the sign-off / undo transition so a
  // fresh attestation tucks itself away and an undo brings the list back.
  const [collapsed, setCollapsed] = useState(signedOff);
  const prevSignedOff = useRef(signedOff);
  useEffect(() => {
    if (signedOff && !prevSignedOff.current) setCollapsed(true);   // just signed off
    if (!signedOff && prevSignedOff.current) setCollapsed(false);  // undone → reopen
    prevSignedOff.current = signedOff;
  }, [signedOff]);

  // True when the step is signed off AND the partner has it collapsed.
  // Drives both the skip-fetch path and the compact render below.
  const collapsedDone = signedOff && collapsed;
  // openAnchor is persisted to sessionStorage (keyed by scope) so the
  // expanded SG survives the post-action reload AND any remount of this
  // panel triggered by parent re-renders (optimistic-state changes in
  // DataQualityPanel). Mirrors how DataQualityPanel persists its own
  // expansion state.
  const SG_OPEN_KEY = `cg.sgReview.openAnchor.${scope}`;
  const [openAnchor, setOpenAnchorRaw] = useState(() => {
    if (typeof window === 'undefined') return null;
    try { return window.sessionStorage.getItem(SG_OPEN_KEY); } catch { return null; }
  });
  const setOpenAnchor = useCallback((next) => {
    setOpenAnchorRaw(next);
    if (typeof window === 'undefined') return;
    try {
      if (next == null) window.sessionStorage.removeItem(SG_OPEN_KEY);
      else window.sessionStorage.setItem(SG_OPEN_KEY, String(next));
    } catch { /* sessionStorage may be unavailable */ }
  }, [SG_OPEN_KEY]);

  // Latest request wins (rapid optimistic actions each trigger a quiet refetch).
  const reqId = useRef(0);
  // `silent` = background reconcile after an action: no "Loading…" flip, no sign-off
  // lock-out, and a failure keeps the list on screen (toast) rather than blanking it.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (gated || collapsedDone || demo) {
      // Behind the gate, signed off and collapsed, or in the spotlight-tour demo
      // — no need to pull the (potentially large) SG list. The sign-off bar (the
      // element the tour points at) still renders below. Expanding the collapsed
      // step flips collapsedDone and re-runs this loader.
      setLoading(false);
      setData(null);
      return;
    }
    const mine = ++reqId.current;
    if (!silent) {
      setLoading(true);
      setErr(null);
    }
    try {
      // period drives the standalone-by-default augment (PE-only cycles show
      // each ungrouped client as its own singleton SG).
      const qs = new URLSearchParams();
      if (scope === 'all') qs.set('scope', 'all');
      if (period) qs.set('period', period);
      const suffix = qs.toString() ? `?${qs}` : '';
      const res = await fetch(`/api/dgSgReview${suffix}`, { credentials: 'include' });
      if (!res.ok) throw new Error(errorText({ message: await res.text() || `HTTP ${res.status}` }));
      const json = await res.json();
      if (mine !== reqId.current) return;
      rememberClientNames(json);
      setData(json);
      setErr(null);
    } catch (e) {
      if (mine !== reqId.current) return;
      if (silent) {
        emitToast({ kind: 'warn', message: `Couldn't refresh Signer Groups: ${e.message}. Showing the last loaded list.`, durationMs: 10000 });
      } else {
        setErr(e.message);
        setData(null);
      }
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [scope, period, gated, collapsedDone, demo]);

  useEffect(() => { load(); }, [load]);

  // Search filter: substring match on anchor or any member's
  // client_code / client_name. Case-insensitive. Whitespace-trim
  // so trailing spaces don't accidentally suppress matches.
  const filtered = useMemo(() => {
    if (!data || !Array.isArray(data.sgs)) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.sgs;
    return data.sgs.filter(sg => {
      if (sg.anchorCode.toLowerCase().includes(q)) return true;
      if (sg.anchorName && sg.anchorName.toLowerCase().includes(q)) return true;
      return (sg.members || []).some(m =>
        (m.code && m.code.toLowerCase().includes(q)) ||
        (m.name && m.name.toLowerCase().includes(q))
      );
    });
  }, [data, search]);

  // Paginate the SG list at 50. Reset to page 1 when the filter/data changes.
  const SG_PAGE_SIZE = 10;
  const [sgPage, setSgPage] = useState(1);
  useEffect(() => { setSgPage(1); }, [search, data]);
  const sgTotalPages = Math.max(1, Math.ceil(filtered.length / SG_PAGE_SIZE));
  const sgSafePage   = Math.min(sgPage, sgTotalPages);
  const pagedSgs     = filtered.slice((sgSafePage - 1) * SG_PAGE_SIZE, sgSafePage * SG_PAGE_SIZE);

  return (
    <section className={`cg-phase ${gated ? 'cg-phase--gated' : ''}`} data-tour="cg-step-3">
      <header className="cg-phase-header">
        <div className="cg-phase-step">Step {stepNumber}</div>
        <div className="cg-phase-title-block">
          <h3 className="cg-phase-title">Signer Group Review</h3>
          <p className="cg-phase-subtitle">
            Browse every active Signer Group and edit any composition
            as needed with the per-member actions inside each group, then
            sign off Signer Groups below to unlock Relationship Groups
            (Step 4).
          </p>
        </div>
        <div className="cg-phase-count">
          {gated
            ? <span className="cg-phase-pill cg-phase-pill--gated">Locked</span>
            : signedOff
              ? <button
                  type="button"
                  className="cg-phase-pill cg-phase-pill--done cg-phase-pill--toggle"
                  onClick={() => setCollapsed(c => !c)}
                  aria-expanded={!collapsed}
                  title={collapsed ? 'Show all Signer Groups' : 'Collapse this step'}
                >
                  <i className="fas fa-check-circle" aria-hidden="true" /> Signed off
                  <span className="cg-phase-pill-chevron">{collapsed ? '▸' : '▾'}</span>
                </button>
              : data
                ? <span className="cg-phase-pill cg-phase-pill--active">
                    {filtered.length === data.sgs.length
                      ? `${data.sgs.length} groups`
                      : `${filtered.length} of ${data.sgs.length} groups`}
                  </span>
                : <span className="cg-phase-pill cg-phase-pill--gated">loading…</span>}
        </div>
      </header>

      {gated ? (
        <>
          <p className="cg-phase-gate-note">
            <i className="fas fa-lock" aria-hidden="true" />{' '}
            <strong>Locked.</strong> {gateReason}
          </p>
          {/* Even while this step is gated (a new Step 1/2 issue appeared after
              sign-off), an already-signed-off SG must still expose the admin
              Undo — otherwise the partner is deadlocked: the earlier-step issue
              can't be fixed until Step 3 is re-opened, but the re-open control
              lives here. PhaseSignoffBar only offers Undo in the signed-off
              state and never lets anyone SIGN OFF while gated. */}
          {signedOff && period && (
            <PhaseSignoffBar
              phase="sg"
              period={period}
              signoff={sgSignoff}
              canSignOff={false}
              onChanged={onSignoffChanged}
              testMode={testMode}
              testSessionId={testSessionId}
              isAdmin={isAdmin}
            />
          )}
        </>
      ) : collapsedDone ? (
        // Signed off + collapsed: compact confirmation strip only. The
        // PhaseSignoffBar shows who/when and (for admins, or other phases)
        // the Undo affordance; "Show all groups" reopens the full list.
        <>
          <p className="cg-phase-gate-note">
            <i className="fas fa-check-circle" aria-hidden="true" />{' '}
            <strong>Signed off.</strong> Signer Groups are attested for this
            period, so this step is collapsed.{' '}
            <button
              type="button"
              className="cg-sg-review-expand"
              onClick={() => setCollapsed(false)}
            >
              Show all groups
            </button>
          </p>
          {period && (
            <PhaseSignoffBar
              phase="sg"
              period={period}
              signoff={sgSignoff}
              canSignOff={true}
              onChanged={onSignoffChanged}
              testMode={testMode}
              testSessionId={testSessionId}
              isAdmin={isAdmin}
            />
          )}
        </>
      ) : (
        <>
          <div className="cg-sg-review-search">
            <input
              type="text"
              className="cg-sg-review-search-input"
              placeholder="Search by parent or child client code / name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search Signer Groups"
            />
            {search && (
              <button
                type="button"
                className="cg-sg-review-search-clear"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >×</button>
            )}
          </div>

          {loading && <p className="cg-resolve-empty">Loading Signer Groups…</p>}
          {err && (
            <div className="cg-error">Couldn't load Signer Groups: {err}</div>
          )}

          {data && !loading && !err && filtered.length === 0 && (
            <p className="cg-resolve-empty">
              {search
                ? `No Signer Groups match "${search.trim()}".`
                : 'No Signer Groups in scope.'}
            </p>
          )}

          <div className="cg-sg-review-list">
            {pagedSgs.map(sg => (
              <SgRow
                key={sg.anchorCode}
                sg={sg}
                expanded={openAnchor === sg.anchorCode}
                onToggle={() => setOpenAnchor(openAnchor === sg.anchorCode ? null : sg.anchorCode)}
                onResolved={() => Promise.all([load({ silent: true }), onDataChanged ? onDataChanged() : null])}
                testMode={testMode}
                testSessionId={testSessionId}
              />
            ))}
          </div>

          {sgTotalPages > 1 && (
            <div className="cg-pager">
              <button type="button" disabled={sgSafePage <= 1} onClick={() => setSgPage(p => Math.max(1, p - 1))}>‹ Prev</button>
              <span className="cg-pager-info">
                {(sgSafePage - 1) * SG_PAGE_SIZE + 1}–{Math.min(sgSafePage * SG_PAGE_SIZE, filtered.length)} of {filtered.length.toLocaleString()}
              </span>
              <button type="button" disabled={sgSafePage >= sgTotalPages} onClick={() => setSgPage(p => Math.min(sgTotalPages, p + 1))}>Next ›</button>
            </div>
          )}

          {/* SG attestation — the gate for the RG phase (Step 4). Always
              available here once Step 3 is unlocked (gate already cleared
              Step 1 + 2). onChanged bubbles to the parent so the gating
              chain (which reads signoffs.sg) re-evaluates. */}
          {period && (
            <PhaseSignoffBar
              phase="sg"
              period={period}
              signoff={sgSignoff}
              canSignOff={!loading && !err}
              onChanged={onSignoffChanged}
              testMode={testMode}
              testSessionId={testSessionId}
              isAdmin={isAdmin}
            />
          )}
        </>
      )}
    </section>
  );
}

// One SG row + its expansion. Header reads anchor code/name + member
// count + a brief entity-type summary; expansion renders MemberCards
// (same chrome as the P1S1 GroupBox).
function SgRow({ sg, expanded, onToggle, onResolved, testMode, testSessionId }) {
  const summary = useMemo(() => entityTypeSummary(sg.members || []), [sg.members]);
  const { pendingCodes } = useOptimistic();
  // The row is "busy" while any of its members (or the anchor) has an
  // in-flight action. markPending/clearPending are driven by the
  // MemberCard action buttons; we just reflect the state visually.
  const busy = useMemo(() => {
    if (!pendingCodes || pendingCodes.size === 0) return false;
    if (pendingCodes.has(sg.anchorCode)) return true;
    return (sg.members || []).some(m => pendingCodes.has(m.code));
  }, [pendingCodes, sg.anchorCode, sg.members]);

  return (
    <div className={`cg-sg-row ${expanded ? 'open' : ''} ${busy ? 'cg-sg-row--busy' : ''}`}>
      <button
        type="button"
        className="cg-sg-row-header"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="cg-twisty">{expanded ? '▾' : '▸'}</span>
        <ClientChip code={sg.anchorCode} name={sg.anchorName} />
        <span className="cg-sg-member-count">
          {sg.members.length} member{sg.members.length === 1 ? '' : 's'}
        </span>
        <span className="cg-sg-summary">{summary}</span>
        {busy && <span className="cg-sg-row-spinner" aria-label="Working…" />}
      </button>
      {expanded && (
        <div className="cg-sg-row-body">
          {busy && (
            <div className="cg-sg-row-busy-overlay" role="status">
              <span className="cg-sg-row-spinner cg-sg-row-spinner--lg" />
              <span className="cg-sg-row-busy-text">Calculating changes…</span>
            </div>
          )}
          <ul className="cg-compare-list">
            {sg.members.map(m => (
              <MemberCard
                key={m.code}
                member={{
                  code:             m.code,
                  name:             m.name,
                  entityType:       m.entityType,
                  partner:          m.partnerName || m.partnerEmail,   // MemberCard shows a name (nameFor() maps an e-mail the roster knows)
                  partnerEmail:     m.partnerEmail,   // MemberCard judges who may Mark NLC from it
                  inActivePeRoster: true,
                }}
                isParent={m.isParent}
                notInRoster={false}
                onResolved={onResolved}
                testMode={testMode}
                testSessionId={testSessionId}
                allowRgActions={false}
                soloGroup={sg.members.length === 1}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Compact entity-type summary for the SG row header, e.g.
// "1 IND, 1 BIZ" or "3 TRUST". Unknown entity types are bucketed as
// "?" so the row still shows something useful.
function entityTypeSummary(members) {
  if (!members || members.length === 0) return '';
  const counts = new Map();
  for (const m of members) {
    const key = (m.entityType || '?').toString();
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([type, n]) => `${n} ${type}`)
    .join(', ');
}
